import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
  Animated,
  FlatList,
  Image,
  ListRenderItemInfo,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import botAvatar from '/asset/chat/bot-avatar.png';
import Icon from '/component/base/Icon';
import {nobel} from '/theme/color/on.color';

import {styles} from '../ChatModal.style';
import {
  CHAT_TEXT,
  getDeleteConversationAccessibilityLabel,
  getOpenConversationAccessibilityLabel,
} from '../constants/chatText.constants';
import {
  ConversationThread,
  HistoryConversationSection,
} from '../types/ChatModal.types';

const PAGE_SIZE = 10;
const LOAD_MORE_SHIMMER_CARD_COUNT = 3;

type FlatItem =
  | {type: 'header'; label: string}
  | {type: 'conversation'; conversation: ConversationThread};

interface ChatHistoryPanelProps {
  isLoading?: boolean;
  hasHistoryConversation: boolean;
  historyConversationSections: HistoryConversationSection[];
  hasMoreHistory?: boolean;
  isLoadingMoreHistory?: boolean;
  activeConversationId: string;
  editingConversationId: string | null;
  editingTitle: string;
  setEditingTitle: (value: string) => void;
  onDeleteAllConversations: () => void;
  onStartNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onSaveConversationTitle: (conversationId: string) => void;
  onCancelEditConversationTitle: () => void;
  onDeleteConversation: (conversationId: string) => void;
  onLoadMoreHistory?: () => void;
  dynamicStyles: any;
  ts: (key: string, fallback?: string) => string;
}

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  isLoading,
  hasHistoryConversation,
  historyConversationSections,
  hasMoreHistory,
  isLoadingMoreHistory,
  activeConversationId,
  editingConversationId,
  editingTitle,
  setEditingTitle,
  onDeleteAllConversations,
  onStartNewConversation,
  onSelectConversation,
  onSaveConversationTitle,
  onCancelEditConversationTitle,
  onDeleteConversation,
  onLoadMoreHistory,
  dynamicStyles,
  ts,
}) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 900,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 900,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();

    return () => {
      animation.stop();
    };
  }, [shimmerAnim]);

  const flatItems = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = [];
    for (const section of historyConversationSections) {
      items.push({type: 'header', label: section.label});
      for (const conversation of section.conversations) {
        items.push({type: 'conversation', conversation});
      }
    }
    return items;
  }, [historyConversationSections]);

  const loadMore = useCallback(() => {
    if (!hasMoreHistory || isLoadingMoreHistory) {
      return;
    }

    onLoadMoreHistory?.();
  }, [hasMoreHistory, isLoadingMoreHistory, onLoadMoreHistory]);

  const renderItem = useCallback(
    ({item}: ListRenderItemInfo<FlatItem>) => {
      if (item.type === 'header') {
        return (
          <Text
            style={[
              styles.historySectionLabel,
              dynamicStyles.historySectionLabel,
            ]}>
            {item.label}
          </Text>
        );
      }

      const {conversation} = item;
      const isActive = conversation.id === activeConversationId;
      const isEditing = editingConversationId === conversation.id;

      if (isEditing) {
        return (
          <View
            style={[
              styles.historyItem,
              isActive
                ? dynamicStyles.historyItemActive
                : dynamicStyles.historyItemInactive,
            ]}>
            <View style={styles.historyItemRow}>
              <View style={styles.historyContentPressArea}>
                <TextInput
                  style={[
                    styles.historyTitleInput,
                    dynamicStyles.historyTitleInput,
                  ]}
                  value={editingTitle}
                  onChangeText={setEditingTitle}
                  onSubmitEditing={() =>
                    onSaveConversationTitle(conversation.id)
                  }
                  autoFocus
                  selectTextOnFocus
                  returnKeyType="done"
                  maxLength={60}
                  placeholder={ts(
                    'chat.renamePlaceholder',
                    CHAT_TEXT.renamePlaceholder,
                  )}
                  placeholderTextColor={nobel}
                />
              </View>
              <View style={styles.historyMetaColumn}>
                <View style={styles.historyItemActions}>
                  <TouchableOpacity
                    onPress={() => onSaveConversationTitle(conversation.id)}
                    style={[
                      styles.historyTextActionButton,
                      dynamicStyles.historyTextActionPrimary,
                    ]}>
                    <Text
                      style={[
                        styles.historyActionText,
                        dynamicStyles.historyActionTextPrimary,
                      ]}>
                      {ts('chat.save', CHAT_TEXT.save)}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={onCancelEditConversationTitle}
                    style={[
                      styles.historyTextActionButton,
                      dynamicStyles.historyTextActionSecondary,
                    ]}>
                    <Text
                      style={[
                        styles.historyActionText,
                        dynamicStyles.historyActionTextDefault,
                      ]}>
                      {ts('chat.cancel', CHAT_TEXT.cancel)}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        );
      }

      return (
        <TouchableOpacity
          onPress={() => onSelectConversation(conversation.id)}
          style={[
            styles.historyItem,
            isActive
              ? dynamicStyles.historyItemActive
              : dynamicStyles.historyItemInactive,
          ]}
          activeOpacity={0.85}
          accessibilityLabel={getOpenConversationAccessibilityLabel(
            conversation.title,
          )}>
          <View style={[styles.historyItemRow, styles.historyItemRowInline]}>
            <View style={styles.historyContentPressArea}>
              <Text
                numberOfLines={1}
                style={[
                  styles.historyItemTitle,
                  isActive
                    ? dynamicStyles.historyItemTitleActive
                    : dynamicStyles.historyItemTitleInactive,
                ]}>
                {conversation.title}
              </Text>
            </View>
            <View style={[styles.historyMetaColumn, styles.historyMetaInline]}>
              <View
                style={[
                  styles.historyItemActions,
                  styles.historyItemActionsInline,
                ]}>
                <TouchableOpacity
                  onPress={event => {
                    event?.stopPropagation?.();
                    onDeleteConversation(conversation.id);
                  }}
                  style={styles.historyActionButton}
                  accessibilityLabel={getDeleteConversationAccessibilityLabel(
                    conversation.title,
                  )}>
                  <Icon
                    name="trash"
                    style={[
                      styles.historyActionIcon,
                      dynamicStyles.historyDeleteActionIcon,
                    ]}
                  />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    [
      activeConversationId,
      dynamicStyles,
      editingConversationId,
      editingTitle,
      onCancelEditConversationTitle,
      onDeleteConversation,
      onSaveConversationTitle,
      onSelectConversation,
      setEditingTitle,
      ts,
    ],
  );

  const keyExtractor = useCallback((item: FlatItem, index: number) => {
    if (item.type === 'header') return `header-${item.label}`;
    return `conv-${item.conversation.id}-${index}`;
  }, []);

  const shimmerOpacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.35, 0.8],
  });

  return (
    <View style={[styles.historyPanel, dynamicStyles.historyPanelBorder]}>
      <View style={styles.historyToolbar}>
        {hasHistoryConversation ? (
          <TouchableOpacity
            onPress={onDeleteAllConversations}
            style={styles.historyToolbarButton}
            accessibilityLabel={
              CHAT_TEXT.deleteAllConversationsAccessibilityLabel
            }>
            <Icon
              name="trash"
              style={[
                styles.historyToolbarIcon,
                dynamicStyles.historyToolbarDeleteIcon,
              ]}
            />
          </TouchableOpacity>
        ) : null}
      </View>
      {isLoading ? (
        <FlatList
          testID="history-shimmer-list"
          data={Array.from({length: PAGE_SIZE}, (_, index) => index)}
          keyExtractor={item => `history-skeleton-${item}`}
          renderItem={() => (
            <Animated.View
              testID="history-shimmer-card"
              style={[
                styles.historyItem,
                dynamicStyles.historyItemInactive,
                styles.historySkeletonCard,
                {opacity: shimmerOpacity},
              ]}>
              <View style={styles.historySkeletonLinePrimary} />
              <View style={styles.historySkeletonLineSecondary} />
            </Animated.View>
          )}
          style={styles.historyScroll}
          contentContainerStyle={styles.historyContent}
          scrollEnabled={false}
          showsVerticalScrollIndicator={false}
        />
      ) : !hasHistoryConversation ? (
        <View style={styles.historyEmptyStateContainer}>
          <View
            style={[
              styles.historyEmptyStateCard,
              dynamicStyles.historyEmptyStateCard,
            ]}>
            <Image source={botAvatar} style={styles.historyEmptyStateAvatar} />
            <Text
              style={[
                styles.historyEmptyStateTitle,
                dynamicStyles.historyEmptyStateTitle,
              ]}>
              {ts('chat.historyEmptyTitle', CHAT_TEXT.historyEmptyTitle)}
            </Text>
            <Text
              style={[
                styles.historyEmptyStateText,
                dynamicStyles.historyEmptyStateText,
              ]}>
              {ts(
                'chat.historyUnavailableMessage',
                CHAT_TEXT.historyUnavailableMessage,
              )}
            </Text>
            <TouchableOpacity
              style={[
                styles.historyEmptyStateButton,
                dynamicStyles.historyEmptyStateButton,
              ]}
              onPress={onStartNewConversation}
              accessibilityLabel={
                CHAT_TEXT.startNewChatFromEmptyStateAccessibilityLabel
              }>
              <Text
                style={[
                  styles.historyEmptyStateButtonText,
                  dynamicStyles.historyEmptyStateButtonText,
                ]}>
                {ts('chat.newConversationCta', CHAT_TEXT.newConversationCta)}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <FlatList
          testID="history-flat-list"
          data={flatItems}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          onEndReached={loadMore}
          onEndReachedThreshold={0.65}
          initialNumToRender={PAGE_SIZE + 1}
          maxToRenderPerBatch={PAGE_SIZE}
          windowSize={5}
          ListFooterComponent={
            isLoadingMoreHistory ? (
              <View
                style={styles.historyLoadMoreIndicator}
                testID="history-load-more-shimmer-list">
                {Array.from(
                  {length: LOAD_MORE_SHIMMER_CARD_COUNT},
                  (_, index) => index,
                ).map(item => (
                  <Animated.View
                    key={`history-load-more-skeleton-${item}`}
                    testID="history-load-more-shimmer-card"
                    style={[
                      styles.historyItem,
                      dynamicStyles.historyItemInactive,
                      styles.historySkeletonCard,
                      {opacity: shimmerOpacity},
                    ]}>
                    <View style={styles.historySkeletonLinePrimary} />
                    <View style={styles.historySkeletonLineSecondary} />
                  </Animated.View>
                ))}
              </View>
            ) : null
          }
          style={styles.historyScroll}
          contentContainerStyle={styles.historyContent}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
};

ChatHistoryPanel.defaultProps = {
  isLoading: false,
  hasMoreHistory: false,
  isLoadingMoreHistory: false,
  onLoadMoreHistory: undefined,
};

export default ChatHistoryPanel;
